import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, forkJoin, switchMap } from 'rxjs';
import { SubscriptionPlan, SubscriptionPlansService } from '../../core/services/subscription-plans.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { AuthService } from '../../core/auth/auth.service';
import { BusinessService } from '../../core/services/business.service';

@Component({ selector: 'app-plan-selection', imports: [], templateUrl: './plan-selection.html', styleUrl: './plan-selection.scss' })
export class PlanSelection implements OnInit {
  private readonly plansService = inject(SubscriptionPlansService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly businesses = inject(BusinessService);
  readonly plans = signal<SubscriptionPlan[]>([]);
  readonly loading = signal(true);
  readonly selecting = signal('');
  readonly error = signal('');
  readonly message = signal('');
  readonly currentSubscription = signal<any | null>(null);

  ngOnInit(): void {
    forkJoin({ status: this.businesses.myApprovalStatus(), plans: this.plansService.list(), subscription: this.plansService.mine() })
      .pipe(finalize(() => this.loading.set(false))).subscribe({
      next: ({ status, plans, subscription }) => {
        if (status.approvalStatus !== 'Approved') { void this.router.navigate(['/business/onboarding']); return; }
        if (subscription?.plan?.name === 'ZepBazaar Internal') { void this.router.navigate(['/business/dashboard']); return; }
        this.plans.set(plans.filter((plan) => plan.isActive));
        this.currentSubscription.set(subscription);
      },
      error: (error) => this.error.set(httpErrorMessage(error, 'Unable to load available plans.')),
    });
  }

  select(plan: SubscriptionPlan): void {
    if (plan.id === this.currentSubscription()?.plan?.id) return;
    this.selecting.set(plan.id); this.error.set(''); this.message.set('');
    this.plansService.selectMine(plan.id).pipe(
      switchMap((result) => result.paymentRequired
        ? [{ result, refreshed: false }]
        : this.auth.refreshSession().pipe(switchMap(() => [{ result, refreshed: true }]))),
      finalize(() => this.selecting.set('')),
    ).subscribe({
      next: ({ result, refreshed }) => {
        if (result.paymentRequired) this.message.set('Plan selected. Complete payment to activate paid access.');
        else {
          this.message.set(this.currentSubscription() ? 'Plan changed successfully. Refreshing your workspace...' : 'Plan activated. Opening your business workspace...');
          if (refreshed) setTimeout(() => window.location.assign('/business/dashboard'), 350);
        }
      },
      error: (error) => this.error.set(httpErrorMessage(error, 'Unable to select this plan.')),
    });
  }
}
